import FolderOutlined from '@mui/icons-material/FolderOutlined';
import LockOpenOutlined from '@mui/icons-material/LockOpenOutlined';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import { Chip, Grid, IconButton, Stack, Switch, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Door, Location, VisitorGroup } from 'graphql/generated/graphqlTypes';
import useDevicesTab from 'hooks/allVisitors/pages/useVisitorsGroups/pages/useDevicesTab';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useMemo, useState } from 'react';
import {
  getFullDoorsForLockGroup,
  getLockGroupsThatLockBelongTo,
  getLockIcon,
  isLockAssignedToVisitorGroup,
  isLockAssignedViaLockGroupToVisitorGroup,
  isLockGroupAssignedToVisitorGroup,
  isLockGroupEmpty
} from 'utils/Lock/Lock';
import { getAllAccessGrantsOfVisitorGroup } from 'utils/VisitorGroup/VisitorGroup';
import { getOverviewDataGridProps } from 'v2/components/DataGrid/getOverviewDataGridProps';
import { renderHeaderWithId } from 'v2/components/DataGrid/renderHeaderWithId';
import EmptyStateOverview from 'v2/components/EmptyStateOverview/EmptyStateOverview';
import SearchAndFilters, { FilterChip, FilterSection } from 'v2/components/SearchAndFilters/SearchAndFilters';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import {
  filterBatteryStatusDefault,
  filterDeviceGroupsDefault,
  filterDeviceTypeDefault,
  filterOfficeModeDefault
} from 'v2/filters/devices/deviceFilterConstants';
import { DEVICE_TYPES, FilterDeviceTypeState } from 'v2/filters/devices/deviceFilterTypes';
import { filterDevices } from 'v2/filters/devices/filterDevices';
import { useSearch } from 'v2/hooks/useSearch/useSearch';
import DeviceGroupDevicesPopover from './DeviceGroupDevicesPopover';

const searchKeys = ['name'];

interface Props {
  visitorGroup: VisitorGroup;
}

const STATUS_COLUMN_WIDTH = 110;
const SWITCH_COLUMN_WIDTH = 72;

const dataGridSx = {
  '& .MuiDataGrid-cell': { display: 'flex', alignItems: 'center' },
  '& .MuiDataGrid-cell[data-field="assigned"]': { padding: 0, overflow: 'visible' },
  '& .MuiDataGrid-columnHeader[data-field="assigned"]': { padding: 0 },
  '& .MuiDataGrid-columnSeparator': { display: 'none' }
};

const DevicesTab: React.FC<Props> = ({ visitorGroup }) => {
  const { translation } = useTranslation();
  const theme = useTheme();
  const { showSnackbar } = useSnackbarContext();
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const [devicesPopoverAnchorEl, setDevicesPopoverAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedDeviceGroup, setSelectedDeviceGroup] = useState<Location | null>(null);
  const {
    deviceGroups,
    deviceGroupsLoading,
    doors,
    doorsLoading,
    assignToAccessGrants,
    assignToAccessGrantsMutationLoading,
    unassignFromAccessGrants,
    unassignFromAccessGrantsMutationLoading
  } = useDevicesTab({
    handleFetchError: (msg) => showSnackbar(msg, { severity: 'error' }),
    visitorGroupId: visitorGroup.id
  });
  const [filterStr, setFilterStr] = useState('');
  const [filterDeviceType, setFilterDeviceType] = useState<FilterDeviceTypeState>(filterDeviceTypeDefault);
  const isDeviceTypeFilterEnabled = Object.values(filterDeviceType).some(Boolean);

  const deviceTypeLabels: Record<keyof FilterDeviceTypeState, string> = {
    reader: translation.reader,
    controller: translation.controller,
    handle: translation.handle,
    swingHandle: translation.swing_handle,
    cylinder: translation.cylinder,
    locker: translation.locker
  };

  const handleDeviceTypeToggle = (deviceType: keyof FilterDeviceTypeState) =>
    setFilterDeviceType((current) => ({ ...current, [deviceType]: !current[deviceType] }));

  const filterSections: FilterSection[] = [
    {
      id: 'device_type',
      header: translation.device_type,
      type: 'checkbox',
      items: DEVICE_TYPES.map((deviceType) => ({
        key: deviceType,
        label: deviceTypeLabels[deviceType],
        checked: filterDeviceType[deviceType],
        onChange: () => handleDeviceTypeToggle(deviceType)
      })),
      isClearDisabled: !isDeviceTypeFilterEnabled,
      onClear: () => setFilterDeviceType(filterDeviceTypeDefault)
    }
  ];

  const filterChips: FilterChip[] = DEVICE_TYPES.filter((deviceType) => filterDeviceType[deviceType]).map(
    (deviceType) => ({
      label: deviceTypeLabels[deviceType],
      onDelete: () => handleDeviceTypeToggle(deviceType)
    })
  );

  const searchedDoors = useSearch({ data: doors, search: filterStr, keys: searchKeys });
  const searchedDeviceGroups = useSearch({ data: deviceGroups, search: filterStr, keys: searchKeys });

  const filteredDoors = useMemo(
    () =>
      filterDevices({
        devices: searchedDoors,
        lockGroups: [],
        filterDeviceGroups: filterDeviceGroupsDefault,
        filterDeviceType,
        filterBatteryStatus: filterBatteryStatusDefault,
        filterOfficeMode: filterOfficeModeDefault
      }),
    [searchedDoors, filterDeviceType]
  );

  const handleSwitchOnClick = async (value: boolean, lockId?: string, lockGroupId?: string) => {
    if (!assignToAccessGrantsMutationLoading && !unassignFromAccessGrantsMutationLoading) {
      const accessGrantsIds = getAllAccessGrantsOfVisitorGroup(visitorGroup).map((accessGrant) => accessGrant.id);
      try {
        value
          ? await assignToAccessGrants(accessGrantsIds, lockId ? [lockId] : [], lockGroupId ? [lockGroupId] : [])
          : await unassignFromAccessGrants(accessGrantsIds, lockId ? [lockId] : [], lockGroupId ? [lockGroupId] : []);
      } catch {
        // The mutations' onError already shows the message; avoid an unhandled rejection from the switch handler.
      }
    }
  };

  const isMutationLoading = assignToAccessGrantsMutationLoading || unassignFromAccessGrantsMutationLoading;

  const isLoading = deviceGroupsLoading || doorsLoading;
  const deviceGroupsHeaderWithId = renderHeaderWithId('device-groups-');
  const locksHeaderWithId = renderHeaderWithId('locks-');

  const deviceGroupColumns: GridColDef<Location>[] = [
    {
      field: 'name',
      headerName: translation.device_groups,
      flex: 1,
      minWidth: 0,
      sortable: false,
      renderHeader: deviceGroupsHeaderWithId,
      renderCell: ({ row }: GridRenderCellParams<Location>) => (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <IconButton
            id={`device-group-${row.id}-devices-button`}
            size="small"
            sx={{ flexShrink: 0 }}
            aria-label={translation.device_groups}
            onClick={(event) => {
              setDevicesPopoverAnchorEl(event.currentTarget);
              setSelectedDeviceGroup(row);
            }}
          >
            <FolderOutlined color="action" />
          </IconButton>
          <Tooltip title={row.name}>
            <Typography variant="body2" noWrap>
              {row.name}
            </Typography>
          </Tooltip>
        </Stack>
      )
    },
    {
      field: 'status',
      headerName: '',
      width: STATUS_COLUMN_WIDTH,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: deviceGroupsHeaderWithId,
      renderCell: ({ row }: GridRenderCellParams<Location>) =>
        isLockGroupEmpty(row.id, deviceGroups) ? (
          <Chip
            variant="filled"
            label={<Typography variant="caption">{translation.empty}</Typography>}
            size="small"
            color="warning"
            icon={<WarningAmberOutlined sx={{ fill: theme.palette.warning.main }} />}
          />
        ) : null
    },
    {
      field: 'assigned',
      headerName: '',
      width: SWITCH_COLUMN_WIDTH,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      renderHeader: deviceGroupsHeaderWithId,
      renderCell: ({ row }: GridRenderCellParams<Location>) => (
        <Switch
          id={`device-group-${row.id}-assigned-switch`}
          checked={isLockGroupAssignedToVisitorGroup(row.id, visitorGroup, deviceGroups)}
          disabled={isMutationLoading}
          onChange={(event) => handleSwitchOnClick(event.target.checked, undefined, row.id)}
          aria-label={row.name}
        />
      )
    }
  ];

  const lockColumns: GridColDef<Door>[] = [
    {
      field: 'name',
      headerName: translation.devices,
      flex: 1,
      minWidth: 0,
      sortable: false,
      renderHeader: locksHeaderWithId,
      renderCell: ({ row }: GridRenderCellParams<Door>) => (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
          <Stack sx={{ flexShrink: 0 }}>{getLockIcon(row, { color: 'action' })}</Stack>
          <Tooltip title={row.name}>
            <Typography variant="body2" noWrap>
              {row.name}
            </Typography>
          </Tooltip>
        </Stack>
      )
    },
    {
      field: 'lockGroups',
      headerName: '',
      flex: 1,
      minWidth: 0,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderHeader: locksHeaderWithId,
      renderCell: ({ row }: GridRenderCellParams<Door>) => {
        const lockGroupNames = getLockGroupsThatLockBelongTo(deviceGroups, row.id)
          .map((lockGroup) => lockGroup.name)
          .join(' , ');

        return (
          <Tooltip title={lockGroupNames}>
            <Typography variant="body2" color="textSecondary" noWrap>
              {lockGroupNames}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: 'assigned',
      headerName: '',
      width: SWITCH_COLUMN_WIDTH,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      renderHeader: locksHeaderWithId,
      renderCell: ({ row }: GridRenderCellParams<Door>) => (
        <Switch
          id={`device-${row.id}-assigned-switch`}
          checked={isLockAssignedToVisitorGroup(row.id, visitorGroup, doors)}
          disabled={isMutationLoading || isLockAssignedViaLockGroupToVisitorGroup(row.id, visitorGroup, deviceGroups)}
          onChange={(event) => handleSwitchOnClick(event.target.checked, row.id)}
          aria-label={row.name}
        />
      )
    }
  ];

  const deviceGroupsDataGridProps = getOverviewDataGridProps({
    rowCount: searchedDeviceGroups.length,
    loading: isLoading
  });
  const locksDataGridProps = getOverviewDataGridProps({ rowCount: filteredDoors.length, loading: isLoading });

  // Keeps the grid's title (same id as its column header) so it stays clear which data has no results.
  const renderNoResultsState = (title: string, headerId: string) => (
    <Stack>
      <Typography id={headerId} variant="subtitle2" color="textPrimary" sx={{ px: 2, py: 1 }}>
        {title}
      </Typography>
      <EmptyStateOverview
        icon={LockOpenOutlined}
        title={translation.no_results}
        content={translation.no_results_search_and_filters_description}
        sx={{ height: 'auto', pt: 8, pb: 4 }}
      />
    </Stack>
  );

  return (
    <Grid
      container
      sx={{ borderRadius: 1, width: '100%', backgroundColor: theme.custom.background.level2 }}
      direction="column"
      wrap="nowrap"
      spacing={3}
    >
      <SearchAndFilters
        filterStr={filterStr}
        onFilterStrChange={setFilterStr}
        sections={filterSections}
        chips={filterChips}
      />
      <Grid
        container
        direction="column"
        spacing={2}
        sx={{ backgroundColor: theme.custom.background.level3 }}
        padding="24px"
      >
        <Typography variant="body1" color="textSecondary">
          {translation.device_group_toggle_cascades_to_devices}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {translation.view_device_group_membership_via_icon}
        </Typography>
        <Grid
          container
          direction={isExtraSmallScreen ? 'column' : 'row'}
          spacing={2}
          columnSpacing={4}
          size={12}
          sx={{ backgroundColor: theme.custom.background.level3 }}
        >
          <Grid container size={6} direction="column" sx={{ minWidth: 0 }}>
            {!isLoading && deviceGroups.length > 0 && searchedDeviceGroups.length === 0 ? (
              renderNoResultsState(translation.device_groups, 'device-groups-header-name')
            ) : (
              <DataGrid
                {...deviceGroupsDataGridProps}
                rows={searchedDeviceGroups}
                className="background-level2 not-clickable"
                columns={deviceGroupColumns}
                sx={dataGridSx}
                slotProps={{
                  ...deviceGroupsDataGridProps.slotProps,
                  row: { idPrefix: 'device-groups-' },
                  cell: { idPrefix: 'device-groups-' }
                }}
              />
            )}
          </Grid>
          <Grid container size={6} direction="column" sx={{ minWidth: 0 }}>
            {!isLoading && doors.length > 0 && filteredDoors.length === 0 ? (
              renderNoResultsState(translation.devices, 'locks-header-name')
            ) : (
              <DataGrid
                {...locksDataGridProps}
                rows={filteredDoors}
                className="background-level2 not-clickable"
                columns={lockColumns}
                sx={dataGridSx}
                slotProps={{
                  ...locksDataGridProps.slotProps,
                  row: { idPrefix: 'devices-' },
                  cell: { idPrefix: 'devices-' }
                }}
              />
            )}
          </Grid>
        </Grid>
      </Grid>
      <DeviceGroupDevicesPopover
        anchorEl={devicesPopoverAnchorEl}
        open={Boolean(devicesPopoverAnchorEl)}
        onClose={() => setDevicesPopoverAnchorEl(null)}
        deviceGroupName={selectedDeviceGroup?.name ?? ''}
        devices={selectedDeviceGroup ? getFullDoorsForLockGroup(doors, selectedDeviceGroup) : []}
      />
    </Grid>
  );
};

export default DevicesTab;
